import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  Loader2,
  RotateCcw,
  Eye,
  DollarSign,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminSubscriptionApi } from './adminApi';

const TX_STATUS_COLORS = {
  succeeded: 'bg-green-100 text-green-800 border-green-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
  refunded: 'bg-purple-100 text-purple-800 border-purple-200',
  partially_refunded: 'bg-orange-100 text-orange-800 border-orange-200',
  disputed: 'bg-red-100 text-red-800 border-red-200',
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${TX_STATUS_COLORS[status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
    {status?.replace(/_/g, ' ')}
  </span>
);

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatCurrency = (amount, currency = 'USD') => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
};

const STATUSES = ['succeeded', 'pending', 'failed', 'refunded', 'partially_refunded', 'disputed'];
const PROVIDERS = ['stripe', 'paypal', 'manual'];

export default function AdminTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({ status: '', provider: '', date_from: '', date_to: '', amount_min: '', amount_max: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Detail modal
  const [detailModal, setDetailModal] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Refund modal
  const [refundModal, setRefundModal] = useState(false);
  const [refundTx, setRefundTx] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (debouncedSearch) params.search = debouncedSearch;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });

      const res = await adminSubscriptionApi.listTransactions(params);
      setTransactions(res.data || res.transactions || []);
      setTotal(res.total || 0);
    } catch (err) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, debouncedSearch, filters]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);
  useEffect(() => { setPage(1); }, [debouncedSearch, filters]);

  const totalPages = Math.ceil(total / perPage) || 1;

  const openDetail = async (tx) => {
    setDetailLoading(true);
    setDetailModal(true);
    try {
      const full = await adminSubscriptionApi.getTransaction(tx.id);
      setSelectedTx(full);
    } catch {
      setSelectedTx(tx);
    } finally {
      setDetailLoading(false);
    }
  };

  const openRefund = (tx, e) => {
    e.stopPropagation();
    setRefundTx(tx);
    setRefundAmount(tx.amount?.toString() || '');
    setRefundReason('');
    setRefundModal(true);
  };

  const handleRefund = async () => {
    if (!refundReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    const amount = parseFloat(refundAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setRefundLoading(true);
    try {
      await adminSubscriptionApi.refund(refundTx.id, amount, refundReason);
      toast.success('Refund processed successfully');
      setRefundModal(false);
      fetchTransactions();
    } catch (err) {
      toast.error('Refund failed');
    } finally {
      setRefundLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });

      const res = await adminSubscriptionApi.exportTransactions(params);
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setFilters({ status: '', provider: '', date_from: '', date_to: '', amount_min: '', amount_max: '' });
    setSearch('');
  };

  const hasActiveFilters = Object.values(filters).some(Boolean) || debouncedSearch;

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Transactions</h1>
          <p className="text-sm text-muted-foreground">{total.toLocaleString()} transaction{total !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors disabled:opacity-50"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export CSV
        </button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by user email or transaction ID..."
                className="w-full pl-10 pr-4 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors ${showFilters ? 'bg-primary text-primary-foreground' : 'border-input bg-background hover:bg-accent'}`}
            >
              <Filter className="h-4 w-4" />
              Filters
            </button>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="inline-flex items-center gap-1 px-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <X className="h-4 w-4" /> Clear
              </button>
            )}
          </div>

          {showFilters && (
            <div className="flex flex-wrap items-end gap-3 pt-2 border-t">
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  className="h-8 px-2 text-sm rounded-md border border-input bg-background"
                >
                  <option value="">All</option>
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Provider</label>
                <select
                  value={filters.provider}
                  onChange={(e) => setFilters((f) => ({ ...f, provider: e.target.value }))}
                  className="h-8 px-2 text-sm rounded-md border border-input bg-background"
                >
                  <option value="">All</option>
                  {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Date From</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                  className="h-8 px-2 text-sm rounded-md border border-input bg-background"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Date To</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                  className="h-8 px-2 text-sm rounded-md border border-input bg-background"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Min Amount</label>
                <input
                  type="number"
                  value={filters.amount_min}
                  onChange={(e) => setFilters((f) => ({ ...f, amount_min: e.target.value }))}
                  placeholder="0"
                  className="h-8 w-24 px-2 text-sm rounded-md border border-input bg-background"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Max Amount</label>
                <input
                  type="number"
                  value={filters.amount_max}
                  onChange={(e) => setFilters((f) => ({ ...f, amount_max: e.target.value }))}
                  placeholder="999"
                  className="h-8 w-24 px-2 text-sm rounded-md border border-input bg-background"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Provider TX ID</TableHead>
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(8)].map((_, j) => (
                      <TableCell key={j}><div className="h-4 bg-muted rounded animate-pulse w-16" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No transactions found</TableCell>
                </TableRow>
              ) : (
                transactions.map((tx) => (
                  <TableRow key={tx.id} className="cursor-pointer" onClick={() => openDetail(tx)}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(tx.created_at)}</TableCell>
                    <TableCell className="font-medium">{tx.user_email || tx.email || '-'}</TableCell>
                    <TableCell className="font-semibold">{formatCurrency(tx.amount, tx.currency)}</TableCell>
                    <TableCell className="text-muted-foreground uppercase text-xs">{tx.currency || 'USD'}</TableCell>
                    <TableCell><StatusBadge status={tx.status} /></TableCell>
                    <TableCell className="text-muted-foreground">{tx.provider || '-'}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground max-w-[140px] truncate">{tx.provider_tx_id || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => openDetail(tx)}
                          className="p-1.5 rounded-md hover:bg-accent transition-colors"
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {(tx.status === 'succeeded' || tx.status === 'completed') && (
                          <button
                            onClick={(e) => openRefund(tx, e)}
                            className="p-1.5 rounded-md hover:bg-red-50 text-red-600 transition-colors"
                            title="Refund"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {Math.min(((page - 1) * perPage) + 1, total)}-{Math.min(page * perPage, total)} of {total}
        </p>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-md border border-input hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="px-3 py-1 text-sm font-medium">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-md border border-input hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Transaction Detail Modal */}
      <Dialog open={detailModal} onOpenChange={setDetailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Transaction Detail
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : selectedTx && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-muted-foreground">ID</p><p className="font-mono text-xs">{selectedTx.id}</p></div>
                <div><p className="text-muted-foreground">Status</p><p><StatusBadge status={selectedTx.status} /></p></div>
                <div><p className="text-muted-foreground">Amount</p><p className="font-semibold">{formatCurrency(selectedTx.amount, selectedTx.currency)}</p></div>
                <div><p className="text-muted-foreground">Currency</p><p className="uppercase">{selectedTx.currency || 'USD'}</p></div>
                <div><p className="text-muted-foreground">User</p><p>{selectedTx.user_email || '-'}</p></div>
                <div><p className="text-muted-foreground">Provider</p><p>{selectedTx.provider || '-'}</p></div>
                <div><p className="text-muted-foreground">Provider TX ID</p><p className="font-mono text-xs break-all">{selectedTx.provider_tx_id || '-'}</p></div>
                <div><p className="text-muted-foreground">Date</p><p>{formatDate(selectedTx.created_at)}</p></div>
                {selectedTx.refunded_amount != null && (
                  <div><p className="text-muted-foreground">Refunded</p><p className="text-red-600">{formatCurrency(selectedTx.refunded_amount, selectedTx.currency)}</p></div>
                )}
                {selectedTx.description && (
                  <div className="col-span-2"><p className="text-muted-foreground">Description</p><p>{selectedTx.description}</p></div>
                )}
                {selectedTx.metadata && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground mb-1">Metadata</p>
                    <pre className="p-3 bg-muted rounded-md text-xs overflow-auto max-h-40">{JSON.stringify(selectedTx.metadata, null, 2)}</pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Refund Modal */}
      <Dialog open={refundModal} onOpenChange={setRefundModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Process Refund
            </DialogTitle>
            <DialogDescription>
              Refund for transaction {refundTx?.provider_tx_id || refundTx?.id}. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium mb-1.5">Original Amount</label>
              <p className="text-lg font-semibold">{formatCurrency(refundTx?.amount, refundTx?.currency)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Refund Amount</label>
              <input
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                step="0.01"
                min="0.01"
                max={refundTx?.amount}
                className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="Provide a reason for this refund..."
                rows={3}
                className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              onClick={() => setRefundModal(false)}
              className="px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleRefund}
              disabled={refundLoading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {refundLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Process Refund
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
